<script lang="ts">
    import {onMount} from 'svelte';
    import Button from '$lib/components/common/button/Button.svelte';
    import RequiredBadge from '$lib/components/common/badges/RequiredBadge.svelte';
    import {showToast} from '$lib/helpers/toastrHelper';
    import {Eye, EyeSlash, Info} from 'phosphor-svelte';
    import {getModalStore, popup} from '@skeletonlabs/skeleton';
    import Tooltip from '$lib/components/common/tooltip/Tooltip.svelte';
    import {GetHoverPopupSettings} from '$lib/components/common/tooltip/utils';
    import {fetchWithAuth} from '$lib/utils/http/httpUtil';
    import {extractErrorMessage} from '$lib/utils/error/errorUtils';

    export let clientId: string = '';
  export let entityId: string = '';
  export let clientName: string = '';
  export let organisationId: string | undefined;

  const modalStore = getModalStore();
  const postingReference2Tooltip = GetHoverPopupSettings('wcbs_posting_reference_2_info');
  const autoUpdateBatchTooltip = GetHoverPopupSettings('wcbs_auto_update_batch_info');

  type Entity = {
    id: number;
    name: string;
    code: string;
    hasWCBSConfig: boolean;
  };

  let entities: Entity[] = [];
  let selectedEntity: Entity | null = null;
  let newEntityName = '';
  let isLoadingEntities = false;
  let isLoading = false;
  let isExecuting = false;
  let isForcingSync = false;
  let hasExistingConfig = false;
  let showConfigSection = false;
  let showSecret = false;
  let urlError = '';

  let wcbsClientId = '';
  let wcbsClientSecret = '';
  let wcbsSchoolCode = '';
  let wcbsRemoteUrl = '';
  let budgetType = 1;
  let invoiceBatchType = 0;
  let autoUpdateBatch = false;
  const defaultPostingReference2 = 0;
  let postingReference2 = defaultPostingReference2;

  type ConfigurationOption = {
    value: number;
    name: string;
    description?: string;
  };

  let budgetTypeOptions: ConfigurationOption[] = [];
  let invoiceBatchTypeOptions: ConfigurationOption[] = [];
  let postingReference2Options: ConfigurationOption[] = [];

  function handleApiError(error: unknown, message: string) {
    if (error instanceof Error) {
      console.error(`${message}:`, error.message);
      showToast({ title: 'Error', message: error.message, type: 'error' });
    } else {
      console.error(`${message}:`, error);
      showToast({ title: 'Error', message, type: 'error' });
    }
  }

  // Convert PascalCase enum names into readable dropdown labels.
  function formatOptionName(name: string): string {
    return name.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  async function loadConfigurationOptions() {
    try {
      const response = await fetchWithAuth('/api/wcbs/options', {
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`Failed to load WCBS options (HTTP ${response.status})`);

      const result = await response.json();
      budgetTypeOptions = result.budgetTypes ?? [];
      invoiceBatchTypeOptions = result.invoiceBatchTypes ?? [];
      postingReference2Options = result.postingReference2Options ?? [];
    } catch (error) {
      handleApiError(error, 'Failed to load WCBS options');
    }
  }

  function validateRemoteUrl(url: string): boolean {
    if (!url) {
      urlError = '';
      return false;
    }

    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.protocol !== 'https:') {
        urlError = 'URL must use https://';
        return false;
      }
    } catch {
      urlError = 'Enter a valid URL';
      return false;
    }

    urlError = '';
    return true;
  }

  $: isUrlValid = validateRemoteUrl(wcbsRemoteUrl);
  $: isSchoolCodeValid = wcbsSchoolCode.trim().length > 0;
  $: hasConfiguredEntities = entities.some((entity) => entity.hasWCBSConfig);
  $: secretIsHidden = wcbsClientSecret === '••••••';
  $: formIsValid = !!(clientId && wcbsClientId.trim() && wcbsClientSecret && isSchoolCodeValid && wcbsRemoteUrl && isUrlValid);
  $: canSave = formIsValid && !isExecuting;
  $: showConfigSection = !!(selectedEntity || newEntityName);

  onMount(async () => {
    await loadConfigurationOptions();
    if (clientId) await loadInitialEntities();
  });

  async function loadInitialEntities() {
    if (!clientId || isLoadingEntities) return;
    isLoadingEntities = true;
    try {
      const response = await fetchWithAuth(`/api/wcbs/entities/${clientId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          entities = result.entities;
          if (entityId) {
            const existing = entities.find((entity) => entity.id.toString() === entityId);
            if (existing) {
              selectedEntity = existing;
              await loadExistingConfig();
            } else {
              entityId = '';
              resetForm();
            }
          }
        } else {
          showToast({ title: 'Error', message: result.error || 'Failed to load entities', type: 'error' });
        }
      }
    } catch (error) {
      handleApiError(error, 'Failed to load entities');
    } finally {
      isLoadingEntities = false;
    }
  }

  async function loadExistingConfig() {
    if (!clientId || !entityId || isLoading) return;
    isLoading = true;
    hasExistingConfig = false;
    try {
      const response = await fetchWithAuth(`/api/wcbs/config/${clientId}/${entityId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.exists) {
          hasExistingConfig = true;
          const config = result.config;
          wcbsClientId = config.wcbsClientId ?? '';
          wcbsClientSecret = config.wcbsClientSecret ?? '';
          wcbsSchoolCode = config.wcbsSchoolCode ?? '';
          wcbsRemoteUrl = config.wcbsRemoteUrl ?? '';
          budgetType = config.budgetType ?? 1;
          invoiceBatchType = config.invoiceBatchType ?? 0;
          autoUpdateBatch = config.autoUpdateBatch ?? false;
          postingReference2 = config.postingReference2 ?? defaultPostingReference2;
          showToast({ title: 'Existing Configuration Loaded', message: 'This entity has existing WCBS configuration.', type: 'info' });
        }
      }
    } catch (error) {
      console.error('Failed to load WCBS config:', error);
    } finally {
      isLoading = false;
    }
  }

  async function saveConfiguration() {
    if (!canSave) return;
    isExecuting = true;
    try {
      const response = await fetchWithAuth('/api/wcbs/setup', {
        method: 'POST',
        body: JSON.stringify({
          clientId: parseInt(clientId),
          entityId: entityId ? parseInt(entityId) : 0,
          entityName: newEntityName || selectedEntity?.name || clientName,
          wcbsClientId,
          wcbsClientSecret,
          wcbsSchoolCode,
          wcbsRemoteUrl,
          budgetType,
          invoiceBatchType,
          autoUpdateBatch,
          postingReference2,
        }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const message = await extractErrorMessage(response, `Failed to save WCBS configuration (HTTP ${response.status})`);
        throw new Error(message);
      }

      const result = await response.json();
      const newEntityId = result.entityId;
      hasExistingConfig = true;
      showToast({ title: 'Success', message: 'WCBS configuration saved successfully', type: 'success' });
      if (!entityId && newEntityId) {
        entityId = newEntityId.toString();
        await loadEntitiesWithoutResetting();
        const created = entities.find((entity) => entity.id === newEntityId);
        if (created) {
          newEntityName = '';
          selectedEntity = created;
        }
      } else {
        await loadEntitiesWithoutResetting();
      }
    } catch (error) {
      handleApiError(error, 'Failed to save WCBS configuration');
    } finally {
      isExecuting = false;
    }
  }

  async function forceSync() {
    isForcingSync = true;
    try {
      const response = await fetchWithAuth(`/api/wcbs/clients/${clientId}/force-sync`, { method: 'POST' });
      showToast(
        response.ok ? { title: 'Success', message: 'WCBS sync has been queued', type: 'success' } : { title: 'Error', message: 'Failed to queue WCBS sync', type: 'error' }
      );
    } catch {
      showToast({ title: 'Error', message: 'Failed to queue WCBS sync', type: 'error' });
    } finally {
      isForcingSync = false;
    }
  }

  async function disconnect() {
    if (!clientId || !entityId || !hasExistingConfig || isExecuting) return;
    const confirmed = await confirmDisconnect(selectedEntity?.name ?? clientName);
    if (!confirmed) return;

    isExecuting = true;
    try {
      const response = await fetchWithAuth(`/api/wcbs/disconnect/${clientId}/${entityId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const message = await extractErrorMessage(response, `Failed to disconnect WCBS integration (HTTP ${response.status})`);
        throw new Error(message);
      }

      showToast({ title: 'Success', message: 'WCBS integration disconnected successfully', type: 'success' });
      resetAll();
      await loadEntitiesWithoutResetting();
    } catch (error) {
      handleApiError(error, 'Failed to disconnect WCBS integration');
    } finally {
      isExecuting = false;
    }
  }

  function confirmDisconnect(entityName: string): Promise<boolean> {
    return new Promise((resolve) => {
      modalStore.trigger({
        type: 'confirm',
        title: 'Disconnect WCBS integration?',
        body: `This will disconnect the WCBS integration for "${entityName}". You can configure it again later.`,
        buttonTextCancel: 'Cancel',
        buttonTextConfirm: 'Disconnect',
        modalClasses: '!max-w-md',
        response: resolve,
      });
    });
  }

  function resetForm() {
    wcbsClientId = '';
    wcbsClientSecret = '';
    wcbsSchoolCode = '';
    wcbsRemoteUrl = '';
    budgetType = 1;
    invoiceBatchType = 0;
    autoUpdateBatch = false;
    postingReference2 = defaultPostingReference2;
    hasExistingConfig = false;
    showSecret = false;
  }

  function resetAll() {
    resetForm();
    selectedEntity = null;
    newEntityName = '';
    entityId = '';
  }

  async function loadEntitiesWithoutResetting() {
    if (!clientId || isLoadingEntities) return;
    isLoadingEntities = true;
    try {
      const response = await fetchWithAuth(`/api/wcbs/entities/${clientId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          const currentEntityId = selectedEntity?.id;
          entities = result.entities;
          if (currentEntityId) {
            const updated = entities.find((entity) => entity.id === currentEntityId);
            if (updated) selectedEntity = updated;
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh entities:', error);
    } finally {
      isLoadingEntities = false;
    }
  }

  async function loadEntities() {
    if (!clientId || isLoadingEntities) return;
    isLoadingEntities = true;
    selectedEntity = null;
    newEntityName = '';
    entityId = '';
    resetForm();
    try {
      const response = await fetchWithAuth(`/api/wcbs/entities/${clientId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          entities = result.entities;
          showToast({ title: 'Success', message: 'Entities refreshed successfully', type: 'success' });
        } else {
          showToast({ title: 'Error', message: result.error || 'Failed to load entities', type: 'error' });
        }
      }
    } catch (error) {
      handleApiError(error, 'Failed to load entities');
    } finally {
      isLoadingEntities = false;
    }
  }

  function handleEntitySelection() {
    if (selectedEntity) {
      newEntityName = '';
      entityId = selectedEntity.id.toString();
      if (selectedEntity.hasWCBSConfig) {
        loadExistingConfig();
      } else {
        resetForm();
      }
    } else {
      entityId = '';
    }
  }

  function handleNewEntityInput() {
    if (newEntityName && (selectedEntity !== null || hasExistingConfig)) {
      selectedEntity = null;
      entityId = '';
      resetForm();
    }
  }
</script>

<div class="p-5">
  <h3 class="mb-4 text-xl font-semibold">WCBS Integration Setup</h3>
  <p class="mb-4 text-sm text-surface-700">Configure WCBS integration for this client using school credentials and settings.</p>

  <div class="mb-4 flex justify-end gap-2">
    <Button
      label={isForcingSync ? 'Queueing...' : 'Force sync'}
      onClick={forceSync}
      disabled={isLoadingEntities || isExecuting || isForcingSync || !hasConfiguredEntities}
      size="small"
    />
  </div>

  <div class="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
    <h4 class="mb-4 font-semibold">Select or Create Entity</h4>
    <p class="mb-4 text-sm text-gray-600">Choose an existing entity or create a new one for WCBS integration.</p>

    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
      <div>
        <label for="wcbs-entity-select" class="mb-2 block text-sm font-medium text-gray-700">Existing Entity</label>
        <div class="flex items-end gap-3">
          <select
            id="wcbs-entity-select"
            bind:value={selectedEntity}
            on:change={handleEntitySelection}
            class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
            disabled={isLoadingEntities || newEntityName !== '' || isExecuting}
          >
            <option value={null}>-- Select Entity --</option>
            {#each entities as entity}
              <option value={entity}>{entity.name}{entity.hasWCBSConfig ? ' (has WCBS config)' : ''}</option>
            {/each}
          </select>
          <Button
            label={isLoadingEntities ? 'Loading...' : 'Refresh'}
            onClick={loadEntities}
            disabled={isLoadingEntities || isExecuting || isForcingSync}
            colour="secondary"
            size="small"
          />
        </div>
        {#if isLoadingEntities}<p class="mt-2 text-sm text-gray-500">Loading entities...</p>{/if}
      </div>

      <div>
        <label for="wcbs-new-entity-name" class="mb-2 block text-sm font-medium text-gray-700">New Entity Name</label>
        <input
          id="wcbs-new-entity-name"
          type="text"
          bind:value={newEntityName}
          on:input={handleNewEntityInput}
          placeholder="Enter new entity name"
          class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
          disabled={selectedEntity !== null || isExecuting}
        />
        <p class="mt-2 text-xs text-gray-500">Leave blank to select an existing entity</p>
      </div>
    </div>
  </div>

  {#if showConfigSection}
    <div class="space-y-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div class="flex items-center justify-between">
        <h4 class="font-semibold">WCBS API Configuration</h4>
        {#if isLoading}
          <span class="text-sm text-gray-500">Loading existing configuration...</span>
        {:else if hasExistingConfig}
          <div class="flex items-center gap-2 rounded-md bg-blue-100 px-3 py-1 text-sm text-blue-700">
            <Info size={16} />
            <span>Updating existing configuration</span>
          </div>
        {/if}
      </div>

      {#if newEntityName}
        <div class="rounded-md bg-green-50 p-3 text-sm text-green-700"><p>Creating new entity: <strong>{newEntityName}</strong></p></div>
      {/if}

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label for="wcbs-client-id" class="mb-2 flex items-center gap-2 text-sm font-medium">WCBS Client ID <RequiredBadge /></label>
          <input
            id="wcbs-client-id"
            type="text"
            bind:value={wcbsClientId}
            placeholder="Enter WCBS client ID"
            class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
            class:border-gray-300={wcbsClientId}
            class:border-primary-200={!wcbsClientId}
            disabled={isExecuting}
          />
        </div>

        <div>
          <label for="wcbs-client-secret" class="mb-2 flex items-center gap-2 text-sm font-medium">WCBS Client Secret <RequiredBadge /></label>
          {#if secretIsHidden}
            <div class="flex items-center gap-2">
              <div class="flex-1 rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm italic text-gray-500">Secret is set but not visible</div>
              <Button
                label="Change"
                onClick={() => {
                  wcbsClientSecret = '';
                  showSecret = false;
                }}
                disabled={isExecuting}
                colour="secondary"
                size="small"
              />
            </div>
          {:else}
            <div class="relative">
              {#if showSecret}
                <input
                  id="wcbs-client-secret"
                  type="text"
                  bind:value={wcbsClientSecret}
                  placeholder="Enter client secret"
                  class="w-full rounded-md border px-3 py-2 pr-10 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
                  class:border-gray-300={wcbsClientSecret}
                  class:border-primary-200={!wcbsClientSecret}
                  disabled={isExecuting}
                />
              {:else}
                <input
                  id="wcbs-client-secret"
                  type="password"
                  bind:value={wcbsClientSecret}
                  placeholder="Enter client secret"
                  class="w-full rounded-md border px-3 py-2 pr-10 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
                  class:border-gray-300={wcbsClientSecret}
                  class:border-primary-200={!wcbsClientSecret}
                  disabled={isExecuting}
                />
              {/if}
              <button
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                on:click={() => (showSecret = !showSecret)}
                disabled={isExecuting}
              >
                {#if showSecret}<EyeSlash size={20} />{:else}<Eye size={20} />{/if}
              </button>
            </div>
          {/if}
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label for="wcbs-school-code" class="mb-2 flex items-center gap-2 text-sm font-medium">WCBS School Code <RequiredBadge /></label>
          <input
            id="wcbs-school-code"
            type="text"
            bind:value={wcbsSchoolCode}
            placeholder="Enter WCBS school code"
            class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
            class:border-gray-300={isSchoolCodeValid}
            class:border-error-500={wcbsSchoolCode && !isSchoolCodeValid}
            class:border-primary-200={!wcbsSchoolCode}
            disabled={isExecuting}
          />
          {#if wcbsSchoolCode && !isSchoolCodeValid}<p class="mt-1 text-xs text-error-500">School code is required</p>{/if}
        </div>

        <div>
          <label for="wcbs-remote-url" class="mb-2 flex items-center gap-2 text-sm font-medium">Remote URL <RequiredBadge /></label>
          <input
            id="wcbs-remote-url"
            type="url"
            bind:value={wcbsRemoteUrl}
            placeholder="https://your-wcbs-host/Wcbs.Api.2026.1.0/api"
            class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2"
            class:border-gray-300={!wcbsRemoteUrl || isUrlValid}
            class:border-error-500={wcbsRemoteUrl && !isUrlValid}
            disabled={isExecuting}
          />
          {#if wcbsRemoteUrl && urlError}<p class="mt-1 text-xs text-error-500">{urlError}</p>{/if}
        </div>
      </div>

      <div>
        <h5 class="mb-3 text-sm font-semibold text-gray-700">Posting Settings</h5>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label for="wcbs-budget-type" class="mb-2 block text-sm font-medium text-gray-700">Budget Type</label>
            <select
              id="wcbs-budget-type"
              bind:value={budgetType}
              class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={isExecuting}
            >
              {#each budgetTypeOptions as option}
                <option value={option.value} title={option.description}>{formatOptionName(option.name)}</option>
              {/each}
            </select>
          </div>

          <div>
            <label for="wcbs-invoice-batch-type" class="mb-2 block text-sm font-medium text-gray-700">Invoice Batch Type</label>
            <select
              id="wcbs-invoice-batch-type"
              bind:value={invoiceBatchType}
              class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={isExecuting}
            >
              {#each invoiceBatchTypeOptions as option}
                <option value={option.value} title={option.description}>{formatOptionName(option.name)}</option>
              {/each}
            </select>
          </div>

          <div>
            <div class="mb-2 flex items-center gap-2">
              <label for="wcbs-posting-reference-2" class="text-sm font-medium text-gray-700">Posting Reference 2</label>
              <button type="button" class="[&>*]:pointer-events-none" aria-label="About Posting Reference 2" use:popup={postingReference2Tooltip}>
                <Info size={16} />
              </button>
            </div>
            <select
              id="wcbs-posting-reference-2"
              bind:value={postingReference2}
              class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={isExecuting}
            >
              {#each postingReference2Options as option}
                <option value={option.value} title={option.description}>{formatOptionName(option.name)}</option>
              {/each}
            </select>
          </div>

          <div class="flex items-center gap-2">
            <label for="wcbs-auto-update-batch" class="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input id="wcbs-auto-update-batch" type="checkbox" bind:checked={autoUpdateBatch} disabled={isExecuting} />
              Auto Update Batch
            </label>
            <button type="button" class="[&>*]:pointer-events-none" aria-label="About Auto Update Batch" use:popup={autoUpdateBatchTooltip}>
              <Info size={16} />
            </button>
          </div>

          <Tooltip config={postingReference2Tooltip}>
            Choose how to populate WCBS Posting Reference 2..
          </Tooltip>

          <Tooltip config={autoUpdateBatchTooltip}>
            Automatically update the WCBS batch when posting.
          </Tooltip>
        </div>
      </div>
    </div>
  {/if}

  <div class="mt-6 flex justify-end gap-3">
    {#if selectedEntity && hasExistingConfig}
      <Button label="Disconnect" onClick={disconnect} disabled={isExecuting} colour="error" />
    {/if}
    {#if showConfigSection}
      <Button
        label={isExecuting ? 'Saving...' : hasExistingConfig ? 'Update Configuration' : 'Save Configuration'}
        onClick={saveConfiguration}
        disabled={!canSave}
        colour="primary"
      />
    {/if}
  </div>
</div>
